import { useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { getStatsRevenue } from '../../lib/api';
import { formatGelExact, pctDelta } from '../../lib/format';
import { KpiCard } from '../../components/stats/KpiCard';
import { BarChart } from '../../components/stats/BarChart';
import { BreakdownList } from '../../components/stats/BreakdownList';
import { SectionFrame } from '../../components/stats/SectionFrame';
import { T } from '../../lib/theme';
import type { RevenueStats } from '../../lib/types';

export function RevenueSection({ start, end }: { start: string; end: string }) {
  const loader = useCallback(
    (signal: AbortSignal) => getStatsRevenue(start, end, signal),
    [start, end],
  );
  const { data, loading, error } = useApi<RevenueStats>(loader);

  return (
    <SectionFrame title="Revenue" loading={loading} error={error}>
      {data && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            <KpiCard
              label="Total revenue"
              value={formatGelExact(data.total)}
              delta={pctDelta(data.total, data.prev_total)}
            />
            <KpiCard
              label="Collected revenue"
              value={formatGelExact(data.collected_total)}
              delta={pctDelta(data.collected_total, data.prev_collected_total)}
            />
            {data.by_type.map((b) => (
              <KpiCard key={b.label} label={b.label} value={formatGelExact(b.value)} />
            ))}
          </div>
          {data.coach_revenue_is_estimated && (
            <div style={{ ...T.caption }}>
              Coach revenue is estimated at {formatGelExact(data.coach_hourly_rate_gel)} per table-hour and is included in total revenue. Payment-method values show collected revenue only.
            </div>
          )}
          <div>
            <div style={{ ...T.label, marginBottom: 6 }}>Daily revenue</div>
            <BarChart data={data.daily.map((d) => ({ label: d.date, value: d.value }))} format={formatGelExact} />
          </div>
          <div>
            <div style={{ ...T.label, marginBottom: 6 }}>Collected revenue by payment method</div>
            <BreakdownList data={data.by_method} format={formatGelExact} />
          </div>
        </>
      )}
    </SectionFrame>
  );
}
